import React, { useEffect, useRef, useState } from 'react';

/* ─── Constants ─────────────────────────────────────────── */

const ENTER_MS   = 1800;
const EXIT_MS    = 700;
const MIN_VERTICAL_SEP = 70;

/* ─── Types ─────────────────────────────────────────────── */

type TransitionPhase = 'enter' | 'idle' | 'exit';
type OrbMode = 'initial' | 'settled';

interface OrbPos {
  topVh: number;
  enterTy: string;
}

/* ─── Helpers ───────────────────────────────────────────── */

function randomOrbPositions(): [OrbPos, OrbPos] {
  const range = () => 5 + Math.random() * 80;
  const a = range();
  let b = range();
  let attempts = 0;
  while (Math.abs(a - b) < MIN_VERTICAL_SEP && attempts++ < 40) b = range();

  const enterTyLeft  = `${110 + Math.random() * 20}vh`;
  const enterTyRight = `-${110 + Math.random() * 20}vh`;

  return [
    { topVh: a, enterTy: enterTyLeft  },
    { topVh: b, enterTy: enterTyRight },
  ];
}

/* ─── Route-transition hook ─────────────────────────────── */

function usePageTransition(): { path: string; phase: TransitionPhase } {
  const [path,  setPath]  = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/',
  );
  const [phase, setPhase] = useState<TransitionPhase>('enter');
  const pending = useRef<string>('');
  const timer   = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const navigate = (next: string) => {
      if (next === window.location.pathname && pending.current === '') return;
      pending.current = next;
      setPhase('exit');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setPath(pending.current);
        pending.current = '';
        setPhase('enter');
        timer.current = setTimeout(() => setPhase('idle'), ENTER_MS);
      }, EXIT_MS);
    };

    const onPop = () => navigate(window.location.pathname);
    window.addEventListener('popstate', onPop);

    const orig = {
      push:    history.pushState.bind(history),
      replace: history.replaceState.bind(history),
    };
    history.pushState = function (...a) {
      orig.push(...(a as Parameters<typeof orig.push>));
      navigate(window.location.pathname);
    };
    history.replaceState = function (...a) {
      orig.replace(...(a as Parameters<typeof orig.replace>));
      navigate(window.location.pathname);
    };

    timer.current = setTimeout(() => setPhase('idle'), ENTER_MS);

    return () => {
      window.removeEventListener('popstate', onPop);
      history.pushState    = orig.push;
      history.replaceState = orig.replace;
      clearTimeout(timer.current);
    };
  }, []);

  return { path, phase };
}

/* ─── AnimatedOrb ───────────────────────────────────────── */
/*
 * Architektura bez konfliktu CSS↔JS:
 *
 * INITIAL (pierwszy wlot):
 *   - rAF NIE działa — tylko jedna animacja CSS (orb-fly-enter)
 *   - `top` ustawione na targetTopVh, transform: translateY od enterTy do 0
 *
 * SETTLED (po wlocie):
 *   - rAF jest JEDYNYM właścicielem `transform`
 *   - `top: 0` — cała pozycja idzie przez transform
 *   - curY inicjalizowany z (targetTopVh * H / 100) — brak skoku
 *   - przy zmianie strony: nowe targetY (±70vh) + targetX (±30vw) = bardzo duży ruch
 */

interface OrbInternalState {
  curX: number;
  curY: number;
  targetX: number;
  targetY: number;
  t0: number | null;
}

const AnimatedOrb: React.FC<{
  side: 'left' | 'right';
  phase: TransitionPhase;
  targetTopVh: number;
  orbMode: OrbMode;
  enterTy: string;
  delay: number;
}> = ({ side, phase, targetTopVh, orbMode, enterTy, delay }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const s = useRef<OrbInternalState>({
    curX: 0,
    curY: 0,
    targetX: 0,
    targetY: 0,
    t0: null,
  });

  /* Parametry driftu — inne dla każdej kuli */
  const drift = useRef({
    xFreq:  side === 'left' ? 0.00028 : 0.00023,
    yFreq:  side === 'left' ? 0.00021 : 0.00026,
    xAmp:   side === 'left' ? 0.16    : 0.19,   // 16–19 % szerokości ekranu
    yAmp:   side === 'left' ? 0.13    : 0.10,   // 10–13 % wysokości ekranu
    xPh:    side === 'left' ? 0       : Math.PI,
    yPh:    side === 'left' ? Math.PI * 0.5 : Math.PI * 1.7,
  });

  /* Uruchom rAF tylko w trybie settled */
  useEffect(() => {
    if (orbMode !== 'settled') return;

    /* Inicjalizacja — kula startuje tam gdzie skończyła CSS fly-in */
    const H = window.innerHeight;
    const startY = (targetTopVh / 100) * H;
    s.current.curX    = 0;
    s.current.curY    = startY;
    s.current.targetX = 0;
    s.current.targetY = startY;
    s.current.t0      = null;

    const d = drift.current;

    const tick = (now: number) => {
      if (s.current.t0 === null) s.current.t0 = now;
      const t = now - s.current.t0;

      const W = window.innerWidth;
      const H = window.innerHeight;

      /* Sinusoidalny drift — ciągły, płynny */
      const driftX = Math.sin(t * d.xFreq + d.xPh) * d.xAmp * W;
      const driftY = Math.sin(t * d.yFreq + d.yPh) * d.yAmp * H;

      /* Lerp do celu — ease-out (wolnieje przy dojeżdżaniu) */
      const spd = 0.032;
      s.current.curX += (s.current.targetX - s.current.curX) * spd;
      s.current.curY += (s.current.targetY - s.current.curY) * spd;

      const el = divRef.current;
      if (el) {
        el.style.transform =
          `translate(${s.current.curX + driftX}px, ${s.current.curY + driftY}px)`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [orbMode]); // celowo pomijamy targetTopVh — obsługujemy go niżej

  /* Aktualizacja celu przy zmianie strony */
  useEffect(() => {
    if (orbMode !== 'settled') return;
    const H = window.innerHeight;
    const W = window.innerWidth;

    /* Przesunięcie o pół ekranu przy zmianie trasy */
    s.current.targetY = (targetTopVh / 100) * H;
    s.current.targetX = (Math.random() - 0.5) * W * 0.50;
  }, [targetTopVh, orbMode]);

  /* ── Wspólny styl kuli ── */
  const isMobile = window.innerWidth < 768;
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    width: isMobile ? '85vw' : '65vw',
    height: isMobile ? '70vh' : '90vh',
    borderRadius: '50%',
    filter: isMobile ? 'blur(60px)' : 'blur(100px)',
    [side]: isMobile ? '-20%' : '-12%',
    background:
      side === 'left'
        ? 'radial-gradient(ellipse at 78% 50%, rgba(20,60,160,0.62) 0%, rgba(10,30,100,0.22) 52%, transparent 74%)'
        : 'radial-gradient(ellipse at 22% 50%, rgba(5,100,155,0.56) 0%, rgba(3,55,95,0.20) 52%, transparent 74%)',
  };

  /* ── INITIAL — używaj animacji wejścia spoza ekranu przy pierwszym ładowaniu ── */
  if (orbMode === 'initial') {
    let animation = '';
    if (phase === 'enter') {
      animation = `orb-fly-enter ${ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms both`;
    } else if (phase === 'exit') {
      animation = `orb-fly-exit ${EXIT_MS}ms cubic-bezier(0.55, 0, 1, 0.45) ${delay}ms both`;
    } else {
      /* idle w initial — delikatny CSS drift żeby nie stała w miejscu */
      animation = `orb-drift-${side} 22s ease-in-out infinite`;
    }

    return (
      <div
        style={{
          ...baseStyle,
          top: `${targetTopVh}vh`,
          willChange: 'transform, opacity',
          '--enter-ty': enterTy,
          '--exit-ty': side === 'left' ? '120vh' : '-120vh',
          animation,
        } as React.CSSProperties}
      />
    );
  }

  /* ── SETTLED — rAF rządzi transformem, top: 0 ── */
  return (
    <div
      ref={divRef}
      style={{
        ...baseStyle,
        top: 0,
        willChange: 'transform',
        /* Zapobiega mignięciu przed pierwszą klatką rAF */
        transform: `translateY(${targetTopVh}vh)`,
        animation: 'orb-color-shift 15s ease-in-out infinite',
      } as React.CSSProperties}
    />
  );
};

/* ─── OrbPair ───────────────────────────────────────────── */

const OrbPair: React.FC<{ phase: TransitionPhase }> = ({ phase }) => {
  const [mode, setMode]       = useState<OrbMode>('initial');
  const [positions, setPos]   = useState<[OrbPos, OrbPos]>(randomOrbPositions);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  useEffect(() => {
    if (mode === 'initial' && phase === 'idle') {
      setMode('settled');
    }
    if (mode === 'settled' && phase === 'exit') {
      /* Nowe pozycje ustawiane przy wyjściu — kule ruszają równocześnie z przejściem */
      setPos(randomOrbPositions());
    }
  }, [phase, mode]);

  // On first load, use enter phase. On route change, use current phase
  const effectivePhase = isFirstLoad ? 'enter' : phase;

  return (
    <>
      <AnimatedOrb side="left"  phase={effectivePhase} targetTopVh={positions[0].topVh} orbMode={mode} enterTy={positions[0].enterTy} delay={0}   />
      <AnimatedOrb side="right" phase={effectivePhase} targetTopVh={positions[1].topVh} orbMode={mode} enterTy={positions[1].enterTy} delay={140} />
    </>
  );
};

/* ─── Keyframes ─────────────────────────────────────────── */

const KEYFRAMES = `
  @keyframes orb-fly-enter {
    from { transform: translateY(var(--enter-ty)) scale(0.6); opacity: 0; }
    to   { transform: translateY(0) scale(1); opacity: 1; }
  }
  @keyframes orb-fly-exit {
    from { transform: translateY(0) scale(1); opacity: 1; }
    to   { transform: translateY(var(--exit-ty)) scale(0.6); opacity: 0; }
  }
  @keyframes orb-drift-left {
    0%,100% { transform: translate(0, 0)       scale(1);    }
    25%      { transform: translate(9%, 16%)    scale(1.06); }
    50%      { transform: translate(-7%, 10%)   scale(0.95); }
    75%      { transform: translate(5%, -8%)    scale(1.03); }
  }
  @keyframes orb-drift-right {
    0%,100% { transform: translate(0, 0)       scale(1);    }
    25%      { transform: translate(-9%, 13%)   scale(1.07); }
    50%      { transform: translate(7%, -11%)   scale(0.94); }
    75%      { transform: translate(-5%, 7%)    scale(1.04); }
  }
  @keyframes orb-color-shift {
    0%,100% { filter: hue-rotate(0deg) brightness(1); }
    33%     { filter: hue-rotate(15deg) brightness(0.95); }
    66%     { filter: hue-rotate(-10deg) brightness(1.05); }
  }
  @keyframes aurora-hue-shift {
    0%,100% { filter: hue-rotate(0deg)  brightness(1);    }
    25%      { filter: hue-rotate(12deg) brightness(0.88); }
    50%      { filter: hue-rotate(-8deg) brightness(0.95); }
    75%      { filter: hue-rotate(20deg) brightness(0.82); }
  }
`;

/* ─── Root ──────────────────────────────────────────────── */

const AnimatedBackground: React.FC = () => {
  const { path, phase } = usePageTransition();

  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Inject keyframes immediately on component mount
  React.useEffect(() => {
    if (document.getElementById('animated-bg-kf')) return;
    const el = document.createElement('style');
    el.id = 'animated-bg-kf';
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  if (prefersReducedMotion) return null;
  // Don't render on home page - InteractiveBackground handles it there
  if (path === '/') return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed', inset: 0, zIndex: -2,
        overflow: 'hidden', pointerEvents: 'none',
        backgroundColor: '#050810',
        animation: 'aurora-hue-shift 28s ease-in-out infinite',
      }}
    >
      <OrbPair phase={phase} />
    </div>
  );
};

export default AnimatedBackground;
